/** Post-move assessment UI — logic lives in postMoveAssessmentCore.js */

import React from "react";
import {
  computeMoveMetrics,
  generatePostMoveAssessment,
  attachPostMoveAssessment,
  refreshPostMoveAssessment,
  fmtPct,
} from "./postMoveAssessmentCore.js";

export {
  computeMoveMetrics,
  generatePostMoveAssessment,
  attachPostMoveAssessment,
  refreshPostMoveAssessment,
};

export function movePctColor(pct, period = "1d") {
  if (pct == null || !Number.isFinite(pct)) return null;
  if (pct < 0) return "#ef4444";
  if (period === "21d" && pct > 75) return "#ef4444";
  if (period === "21d" && pct > 40) return "#f97316";
  if ((period === "21d" && pct > 20) || (period === "5d" && pct > 15) || (period === "63d" && pct > 100)) return "#f59e0b";
  return "#22c55e";
}

export function getWatchlistAssessmentPill(result) {
  const a = result?.postMoveAssessment;
  if (!a) return null;
  if (a.positionGuidance === "NEAR_T1") {
    return { text: "💰 Near T1 — Consider partial", color: "#22c55e", bg: "#22c55e18" };
  }
  if (a.riskLevel === "HIGH") {
    const pct = result.move21d ?? result.move5d;
    return { text: `🔥 Parabolic ${fmtPct(pct, false)}`, color: "#f97316", bg: "#f9731618" };
  }
  if (a.riskLevel === "ELEVATED") {
    const pct = result.move21d ?? result.move5d ?? result.move1d;
    return { text: `⚠ Extended ${fmtPct(pct, false)}`, color: "#f59e0b", bg: "#f59e0b18" };
  }
  return null;
}

export function PostMoveAssessmentPanel({ result, T, signalLabel }) {
  const a = result?.postMoveAssessment;
  if (!a) return null;

  const showElevated = a.riskLevel === "ELEVATED" || a.riskLevel === "HIGH";
  const showMomentum = !showElevated && a.entryGuidance === "MOMENTUM_ENTRY_OK" && a.guidanceText;

  if (!showElevated && !showMomentum) return null;

  const sigLabel = signalLabel || a.verdictLabel || "—";
  const isHigh = a.riskLevel === "HIGH";
  const borderColor = showMomentum ? "#22c55e" : isHigh ? "#f97316" : "#f59e0b";
  const bg = showMomentum ? `${T.green}10` : isHigh ? "#f9731610" : "#f59e0b10";
  const title = showMomentum ? "📈 MOMENTUM ASSESSMENT" : "⚠ POST-MOVE ASSESSMENT";
  const ema20Val = result.ema20?.value ?? result.ema20 ?? null;
  const rvolTxt = result.rvol?.ratio != null ? `${result.rvol.ratio.toFixed(1)}x` : "—";
  const highTxt = result.recentHigh20 != null ? `$${result.recentHigh20.toFixed(2)}` : (result.price != null ? `$${result.price.toFixed(2)}` : "—");

  let entryLine = null;
  if (showElevated && a.guidanceText) {
    if (a.entryGuidance === "DO_NOT_CHASE") {
      entryLine = ema20Val != null
        ? `Do not chase — wait for pullback to EMA20 $${Number(ema20Val).toFixed(2)} before new position`
        : "Do not chase — elevated extension; wait for pullback before new position";
    } else if (a.entryGuidance === "WAIT_FOR_PULLBACK") {
      entryLine = ema20Val != null
        ? `Wait for pullback to EMA20 $${Number(ema20Val).toFixed(2)} before new position`
        : "Wait for pullback to support before new position";
    } else {
      entryLine = a.guidanceText;
    }
  }

  let positionLine = null;
  if (a.positionText) {
    if (a.positionGuidance === "NEAR_T1" && result.t1 != null) {
      positionLine = `Consider 50% profit at T1 $${Number(result.t1).toFixed(2)}`;
    } else {
      positionLine = a.positionText;
    }
  }

  return (
    <div style={{
      flexShrink: 0,
      margin: "0 0 0 0",
      padding: "10px 14px",
      background: bg,
      borderBottom: `1px solid ${borderColor}44`,
      borderLeft: `3px solid ${borderColor}`,
    }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: borderColor, letterSpacing: "0.06em", marginBottom: 8 }}>
        {title}
      </div>
      {showMomentum ? (
        <div style={{ fontSize: 10, color: T.textSub, lineHeight: 1.55, marginBottom: 8 }}>
          Up {fmtPct(result.move1d)} today on {rvolTxt} volume
        </div>
      ) : a.context?.length > 0 ? (
        <div style={{ fontSize: 10, color: T.textSub, lineHeight: 1.55, marginBottom: 8 }}>
          {a.context.slice(0, 4).join(" · ")}
        </div>
      ) : null}
      <div style={{ fontSize: 10, color: T.text, lineHeight: 1.6 }}>
        <div><strong>Signal:</strong> {showMomentum ? sigLabel : `Still ${sigLabel}`} ✅</div>
        {showMomentum && (
          <>
            <div style={{ marginTop: 4, color: T.textSub }}>Momentum entry acceptable</div>
            <div style={{ marginTop: 4, color: T.textSub }}>Watch for continuation above {highTxt}</div>
          </>
        )}
        {entryLine && (
          <div style={{ marginTop: 6, color: T.textSub }}>
            <strong>Entry:</strong> {entryLine}
          </div>
        )}
        {positionLine && (
          <div style={{ marginTop: 6, color: T.textSub }}>
            <strong>If in position:</strong> {positionLine}
          </div>
        )}
      </div>
    </div>
  );
}
